import { useEffect, useRef, useState } from 'react'

import FacciaMascotte from './FacciaMascotte'
import { IS_TOUCH } from '../../config/touch'
import {
  helpOpenStore, statoGumballStore, useStore,
  selectedProductStore, watchOpenStore, cartOpenStore, touchMove,
} from '../../store/uiStore'

// ============================================================
// IL TUTORIAL: i comandi li spiega la mascotte, un passo alla volta,
// GUARDANDO SE LI FAI.
//
// Prima era una tendina con l'elenco di tutti i comandi: si apriva sopra
// il gioco, la si leggeva (o no) e la si chiudeva. Chiesto di farla
// diventare un tutorial da videogioco, con la mascotte che ti accompagna e
// vede che premi i tasti giusti.
//
// DUE CONSEGUENZE DI PROGETTO, ed erano obbligate:
//  - NON PUO' ESSERE UNA TENDINA. Se il pannello copre lo schermo e i
//    click li mangia lui, il giocatore non puo' fare quello che gli si sta
//    chiedendo — e il primo passo e' proprio cliccare sul negozio per
//    agganciare la visuale. Quindi e' una striscia in basso che NON prende
//    i click (`pointer-events: none`), tranne i due bottoni.
//  - IL PASSO SI CHIUDE SUL FATTO, NON SUL TASTO, dove i due si possono
//    distinguere. «Premi E su un prodotto» non si considera fatto perche'
//    hai premuto E: si considera fatto quando il prodotto e' davvero in
//    mano. Altrimenti si insegna a premere un tasto, non a giocare.
//    Per muoversi invece il tasto E' il fatto, e li' si spuntano i quattro
//    tasti uno per uno, che e' anche la parte che diverte.
//
// L'elenco completo dei comandi resta, ma come RIEPILOGO alla fine: chi
// vuole solo controllare un tasto lo trova senza rifare il giro (l'app
// "Comandi" dell'orologio riapre di qui, e si puo' saltare al riepilogo).
// ============================================================

const HELP_SEEN_KEY = 'momoz_help_seen'

// `tasti`: quelli da spuntare uno a uno (il passo finisce quando sono
// tutti verdi). `code` accetta piu' varianti separate da |, perche' Shift
// e Ctrl hanno un codice per lato.
// `fatto`: se c'e', il passo si chiude su QUELLO e i tasti sono solo
// disegnati come promemoria.
const PASSI_DESKTOP = [
  {
    id: 'mira',
    testo: 'Ciao! Sono la mascotte del negozio. Comincia cliccando qui sul negozio: da lì in poi la visuale la muovi tu.',
    tasti: [{ tag: 'click' }],
    fatto: 'lock',
  },
  {
    id: 'guarda',
    testo: 'Bene. Ora muovi il mouse e guardati intorno.',
    tasti: [{ tag: 'mouse' }],
    fatto: 'mouse',
  },
  {
    id: 'cammina',
    testo: 'Si cammina con questi quattro. Provali tutti, te li spunto io.',
    tasti: [
      { code: 'KeyW', lab: 'W' }, { code: 'KeyA', lab: 'A' },
      { code: 'KeyS', lab: 'S' }, { code: 'KeyD', lab: 'D' },
    ],
  },
  {
    id: 'corri',
    testo: 'Shift per correre, Ctrl per abbassarti: in basso ci sono ripiani che da in piedi non vedi.',
    tasti: [
      { code: 'ShiftLeft|ShiftRight', lab: 'Shift' },
      { code: 'ControlLeft|ControlRight', lab: 'Ctrl' },
    ],
  },
  {
    id: 'prendi',
    testo: 'Adesso il bello: punta un prodotto sullo scaffale e premi E per prendertelo in mano.',
    tasti: [{ code: 'KeyE', lab: 'E' }],
    fatto: 'prodotto',
  },
  {
    id: 'orologio',
    testo: 'Ultima cosa: premi Q e alzi il polso. Dall’orologio cerchi, apri il carrello e l’account.',
    tasti: [{ code: 'KeyQ', lab: 'Q' }],
    fatto: 'orologio',
  },
]

// Su touch i tasti non ci sono: i passi sono altri e sono meno. Dire
// «premi Shift» a chi ha un telefono in mano non e' solo brutto, e' un
// consiglio sbagliato.
const PASSI_TOUCH = [
  {
    id: 'guarda',
    testo: 'Ciao! Sono la mascotte del negozio. Trascina il dito sullo schermo per guardarti intorno.',
    tasti: [{ tag: 'trascina' }],
    fatto: 'tocco',
  },
  {
    id: 'cammina',
    testo: 'Per muoverti c’è il joystick in basso a sinistra. Spingilo a fondo e ti metti a correre.',
    tasti: [{ tag: 'joystick' }],
    fatto: 'joystick',
  },
  {
    id: 'prendi',
    testo: 'Adesso il bello: inquadra un prodotto sullo scaffale e tocca “Guarda”.',
    tasti: [{ tag: 'Guarda' }],
    fatto: 'prodotto',
  },
  {
    id: 'orologio',
    testo: 'Ultima cosa: tocca l’orologio in alto a destra. Lì dentro ci sono ricerca, carrello e account.',
    tasti: [{ tag: 'orologio' }],
    fatto: 'orologio',
  },
]

// Il riepilogo finale: e' l'elenco di prima, che resta utile a chi vuole
// solo ricontrollare un tasto.
const RIEPILOGO_DESKTOP = [
  { keys: ['W', 'A', 'S', 'D'], desc: 'muoviti nel negozio' },
  { keys: ['Shift'], desc: 'corri' },
  { keys: ['Ctrl'], desc: 'tienilo premuto per abbassarti' },
  { keys: ['E'], desc: 'prendi il prodotto nel mirino, e di nuovo per rimetterlo' },
  { keys: [], tag: '⟳', desc: 'il pomello nella scheda del prodotto lo gira' },
  { keys: ['Q'], desc: 'alza il polso: ricerca, carrello, account' },
  { keys: ['←', '→'], desc: 'scorri le app dell’orologio; Invio apre quella al centro' },
  { keys: ['C'], desc: 'apri e chiudi il carrello' },
  { keys: ['E'], desc: 'al bancone, davanti al POS: stampa lo scontrino e paga' },
  { keys: ['E'], desc: 'sulla macchinetta delle palline: gira la manopola', solo: 'gumball' },
  { keys: ['Esc'], desc: 'chiudi le finestre' },
]

const RIEPILOGO_TOUCH = [
  { keys: [], tag: 'joystick', desc: 'muoviti — spingilo a fondo per correre' },
  { keys: [], tag: 'trascina', desc: 'guardati intorno' },
  { keys: [], tag: 'Guarda', desc: 'inquadra un prodotto e tocca il bottone' },
  { keys: [], tag: '⟳', desc: 'il pomello nella scheda del prodotto lo gira' },
  { keys: [], tag: 'orologio', desc: 'in alto a destra: cerca, carrello, comandi e account' },
  { keys: [], tag: 'Paga', desc: 'al bancone, vicino al POS, per lo scontrino' },
  { keys: [], tag: 'Gira', desc: 'sulla macchinetta delle palline', solo: 'gumball' },
]

const PASSI = IS_TOUCH ? PASSI_TOUCH : PASSI_DESKTOP

export default function Tutorial() {
  const aperto = useStore(helpOpenStore)
  // la riga dei comandi della macchinetta si mostra solo se ci si puo'
  // giocare: elencare un tasto che non fa niente e' peggio che tacere
  const gumball = useStore(statoGumballStore).funziona
  // indice del passo; PASSI.length = riepilogo finale
  const [passo, setPasso] = useState(0)
  const [spuntati, setSpuntati] = useState([])
  // scatta a ogni passo superato: fa sussultare la mascotte
  const [esulta, setEsulta] = useState(0)
  // il volo finale: { dx, dy, s, x, y, w } o null
  const [volo, setVolo] = useState(null)
  const mascotte = useRef(null)

  const chiudi = () => {
    helpOpenStore.set(false)
    localStorage.setItem(HELP_SEEN_KEY, '1')
  }

  // ALLA FINE LA MASCOTTE SE NE VA AL SUO POSTO, in basso a sinistra, invece
  // di sparire con la striscia. Chiesto, e ha un senso oltre al gusto: e' il
  // modo di dire DOVE la si ritrova quando serve.
  // IL BERSAGLIO SI MISURA, NON SI CALCOLA: si chiede alla palla della guida
  // dov'e' davvero, invece di rifare il conto dei suoi 20px d'angolo e dei
  // suoi 58 di lato. Cosi' non ci sono due posti che devono restare d'accordo
  // — se un domani la guida si sposta, il volo la segue da solo — e non si
  // dipende da `window.innerHeight`, che non sempre e' il viewport di layout.
  const VOLO_MS = 880
  const volaVia = () => {
    const r = mascotte.current?.getBoundingClientRect()
    const meta = document.querySelector('.guida:not(.tut-mascotte):not(.tut-volo) .guida-palla')
      ?.getBoundingClientRect()
    if (!r || !meta) { chiudi(); return }
    setVolo({
      x: r.left, y: r.top, w: r.width,
      dx: meta.left - r.left,
      dy: meta.top - r.top,
      s: meta.width / r.width,
    })
    setTimeout(chiudi, VOLO_MS)
  }

  // prima visita: parte da solo
  useEffect(() => {
    if (!localStorage.getItem(HELP_SEEN_KEY)) helpOpenStore.set(true)
  }, [])

  // riaprendolo dall'app "Comandi" si ricomincia da capo
  useEffect(() => {
    if (aperto) { setPasso(0); setSpuntati([]) }
  }, [aperto])

  const corrente = passo < PASSI.length ? PASSI[passo] : null

  // ------------------------------------------------------------
  // GUARDA SE LO FAI. Un solo effetto per passo: si iscrive a quello
  // che serve a QUESTO passo e si stacca appena e' superato, cosi' non
  // restano ascoltatori appesi per tutta la partita.
  // ------------------------------------------------------------
  const avanti = useRef(null)
  avanti.current = () => {
    setEsulta((n) => n + 1)
    // un attimo per far vedere la spunta verde, poi il passo dopo
    setTimeout(() => { setPasso((p) => p + 1); setSpuntati([]) }, 620)
  }

  useEffect(() => {
    if (!aperto || !corrente) return undefined
    // UNA VOLTA SOLA. Chi guarda spara a raffica — il mousemove arriva
    // sessanta volte al secondo, e passata la soglia ognuno chiamerebbe
    // `fatto()` — e ogni chiamata accoda un avanzamento: al primo
    // movimento un po' lungo il tutorial saltava dal passo 2 alla fine.
    // Visto in prova, non dedotto.
    let chiuso = false
    const fatto = () => { if (chiuso) return; chiuso = true; avanti.current() }
    const via = []

    // 1) i tasti da spuntare uno a uno
    if (!corrente.fatto && corrente.tasti.some((t) => t.code)) {
      const onKey = (e) => {
        const t = corrente.tasti.find((x) => x.code && x.code.split('|').includes(e.code))
        if (!t) return
        setSpuntati((s) => {
          if (s.includes(t.lab)) return s
          const nuovi = [...s, t.lab]
          if (nuovi.length >= corrente.tasti.filter((x) => x.code).length) fatto()
          return nuovi
        })
      }
      window.addEventListener('keydown', onKey)
      via.push(() => window.removeEventListener('keydown', onKey))
    }

    // 2) i passi che si chiudono sul FATTO
    if (corrente.fatto === 'lock') {
      // vale l'aggancio della visuale, MA ANCHE UN CLICK QUALUNQUE sul
      // mondo: il pointer lock lo puo' rifiutare il browser (o l'utente),
      // e legare il primo passo solo a quello vorrebbe dire lasciare
      // piantato al passo 1 chi non riesce ad agganciarlo. Il click c'e'
      // stato, la cosa chiesta e' stata fatta: si va avanti.
      const onLock = () => { if (document.pointerLockElement) fatto() }
      const onClick = (e) => { if (e.target instanceof HTMLCanvasElement) fatto() }
      document.addEventListener('pointerlockchange', onLock)
      document.addEventListener('click', onClick)
      via.push(() => document.removeEventListener('pointerlockchange', onLock))
      via.push(() => document.removeEventListener('click', onClick))
    }
    if (corrente.fatto === 'mouse') {
      // non basta UN mousemove: si chiede di guardarsi intorno davvero,
      // quindi si accumula lo spostamento. Agganciati lo da' il movimento
      // grezzo, sganciati si misura di quanto si e' spostato il cursore:
      // cosi' il passo si chiude comunque.
      let quanto = 0, prec = null
      const onMove = (e) => {
        if (document.pointerLockElement) {
          quanto += Math.abs(e.movementX || 0) + Math.abs(e.movementY || 0)
        } else {
          if (prec) quanto += Math.abs(e.clientX - prec.x) + Math.abs(e.clientY - prec.y)
          prec = { x: e.clientX, y: e.clientY }
        }
        if (quanto > 600) fatto()
      }
      window.addEventListener('mousemove', onMove)
      via.push(() => window.removeEventListener('mousemove', onMove))
    }
    if (corrente.fatto === 'tocco') {
      let quanto = 0, prec = null
      const onTouch = (e) => {
        const t = e.touches[0]
        if (!t) return
        if (prec) quanto += Math.abs(t.clientX - prec.x) + Math.abs(t.clientY - prec.y)
        prec = { x: t.clientX, y: t.clientY }
        if (quanto > 400) fatto()
      }
      window.addEventListener('touchmove', onTouch)
      via.push(() => window.removeEventListener('touchmove', onTouch))
    }
    if (corrente.fatto === 'joystick') {
      // touchMove e' un oggetto piatto scritto a ogni frame, non uno store:
      // si guarda a intervalli invece di iscriversi
      const t = setInterval(() => {
        if (Math.hypot(touchMove.x, touchMove.y) > 0.4) fatto()
      }, 120)
      via.push(() => clearInterval(t))
    }
    if (corrente.fatto === 'prodotto') {
      via.push(selectedProductStore.subscribe(() => {
        if (selectedProductStore.get()) fatto()
      }))
    }
    if (corrente.fatto === 'orologio') {
      via.push(watchOpenStore.subscribe(() => { if (watchOpenStore.get()) fatto() }))
      // su desktop vale anche il carrello: chi apre C ha capito lo stesso
      via.push(cartOpenStore.subscribe(() => { if (cartOpenStore.get()) fatto() }))
    }

    return () => via.forEach((f) => f && f())
  }, [aperto, passo, corrente])

  if (!aperto) return null

  const riepilogo = (IS_TOUCH ? RIEPILOGO_TOUCH : RIEPILOGO_DESKTOP)
    .filter((c) => c.solo !== 'gumball' || gumball)

  return (
    <div className="tut">
      <div className={'tut-card' + (corrente ? '' : ' fine') + (volo ? ' via' : '')}>
        {/* LA MASCOTTE: e' lo stesso disegno della guida nell'angolo, con le
            stesse classi, cosi' eredita galleggiamento, sussulto e occhi
            vivi senza riscriverne una riga. Cambia solo dove sta. */}
        <div className="guida tut-mascotte parla" key={esulta} ref={mascotte} aria-hidden="true">
          <svg className="guida-palla" viewBox="0 0 220 220">
            <g className="guida-sussulto">
              <circle className="guida-corpo" cx="110" cy="110" r="78" />
              <FacciaMascotte />
            </g>
          </svg>
        </div>

        <div className="tut-corpo">
          {corrente ? (
            <>
              <div className="tut-passo">
                Passo {passo + 1} di {PASSI.length}
              </div>
              <p className="tut-frase">{corrente.testo}</p>
              <div className="tut-tasti">
                {corrente.tasti.map((t) => {
                  const ok = t.lab && spuntati.includes(t.lab)
                  return t.lab ? (
                    <kbd key={t.lab} className={ok ? 'ok' : ''}>
                      {t.lab}{ok && <span className="tut-ok">✓</span>}
                    </kbd>
                  ) : (
                    <span className="tut-tag" key={t.tag}>{t.tag}</span>
                  )
                })}
              </div>
            </>
          ) : (
            <>
              <div className="tut-passo">Tutto qui. Buona spesa!</div>
              <ul className="tut-riepilogo">
                {riepilogo.map(({ keys, tag, desc }) => (
                  <li key={desc}>
                    <span className="tut-riep-keys">
                      {keys.map((k) => <kbd key={k}>{k}</kbd>)}
                      {tag && <span className="tut-tag">{tag}</span>}
                    </span>
                    <span>{desc}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="tut-azioni">
          {corrente ? (
            <>
              <button className="tut-salta" onClick={() => setPasso(PASSI.length)}>
                salta
              </button>
              <div className="tut-pallini">
                {PASSI.map((p, i) => (
                  <span key={p.id} className={i < passo ? 'fatto' : i === passo ? 'ora' : ''} />
                ))}
              </div>
            </>
          ) : (
            <button className="tut-chiudi" onClick={volaVia}>Si parte!</button>
          )}
        </div>
      </div>

      {/* LA PALLA CHE VOLA VIA: una copia, fissa sullo schermo, che parte da
          dove stava nella striscia e atterra nell'angolo. Una copia e non
          quella di prima perche' dentro la striscia e' in mezzo a un flex, e
          da li' non si va da nessuna parte. */}
      {volo && (
        <div
          className="guida tut-volo"
          aria-hidden="true"
          style={{
            left: volo.x, top: volo.y, width: volo.w, height: volo.w,
            '--dx': `${volo.dx}px`, '--dy': `${volo.dy}px`, '--s': volo.s,
          }}
        >
          <svg className="guida-palla" viewBox="0 0 220 220">
            <g className="guida-sussulto">
              <circle className="guida-corpo" cx="110" cy="110" r="78" />
              <FacciaMascotte />
            </g>
          </svg>
        </div>
      )}
    </div>
  )
}
